import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ROUTES from '../route';

const Feature = ({ icon, title, description }) => (
    <div className="bg-white rounded-2xl border border-warm-200 shadow-sm p-6 flex flex-col items-start gap-3">
        <span className="text-3xl">{icon}</span>
        <h3 className="font-serif text-lg font-semibold text-warm-800">{title}</h3>
        <p className="text-warm-500 text-sm leading-relaxed">{description}</p>
    </div>
);

const Step = ({ number, title, description }) => (
    <div className="flex gap-4 items-start">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-warm-600 text-white font-bold text-sm flex items-center justify-center">
            {number}
        </div>
        <div>
            <h4 className="font-semibold text-warm-800 mb-1">{title}</h4>
            <p className="text-warm-500 text-sm leading-relaxed">{description}</p>
        </div>
    </div>
);

const Landing = () => {
    const navigate = useNavigate();

    useEffect(() => {
        if (localStorage.getItem('api_token')) {
            navigate(ROUTES.HOME);
        }
    }, [navigate]);

    return (
        <div className="min-h-screen bg-warm-50">
            {/* Nav */}
            <header className="bg-parchment border-b border-warm-200 shadow-sm px-6 py-3 flex items-center justify-between sticky top-0 z-30">
                <div className="flex items-center gap-2 font-serif text-xl font-bold text-warm-800">
                    <span className="text-2xl">📚</span>
                    My Books Library
                </div>
                <div className="flex items-center gap-3">
                    <Link
                        to={ROUTES.LOGIN}
                        className="text-warm-600 hover:text-warm-800 font-medium text-sm transition-colors"
                    >
                        Sign In
                    </Link>
                    <Link
                        to={ROUTES.REGISTER}
                        className="bg-warm-600 hover:bg-warm-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors"
                    >
                        Get Started
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-4xl mx-auto px-6 py-20 text-center">
                <div className="text-7xl mb-6">📚</div>
                <h1 className="font-serif text-5xl sm:text-6xl font-bold text-warm-800 leading-tight mb-6">
                    Your Personal<br />
                    <span className="text-warm-500">Reading Universe</span>
                </h1>
                <p className="text-warm-500 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-10">
                    Track what you've read, discover new books, rate your favourites, and build the perfect library — all in one cozy place.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                        to={ROUTES.REGISTER}
                        className="bg-warm-600 hover:bg-warm-700 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-colors shadow-md"
                    >
                        Create Free Account
                    </Link>
                    <Link
                        to={ROUTES.LOGIN}
                        className="bg-white hover:bg-warm-100 text-warm-700 font-semibold px-8 py-4 rounded-xl text-lg transition-colors border border-warm-200 shadow-sm"
                    >
                        Sign In
                    </Link>
                </div>
            </section>

            {/* Features */}
            <section className="bg-white border-y border-warm-200 py-16">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="font-serif text-3xl font-bold text-warm-800 text-center mb-3">
                        Everything a book lover needs
                    </h2>
                    <p className="text-warm-400 text-center mb-12 text-sm">
                        From discovery to your last page, we've got you covered.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <Feature
                            icon="📖"
                            title="Track Your Progress"
                            description="Set reading progress on every book from 0 to 100%. Know exactly where you left off and celebrate when you finish."
                        />
                        <Feature
                            icon="⭐"
                            title="Rate & Review"
                            description="Score every book out of 10 and keep personal notes tied to each one. Your thoughts, preserved forever."
                        />
                        <Feature
                            icon="🔍"
                            title="Discover via Google Books"
                            description="Search millions of titles through the Google Books API. Found something you love? Add it to your library in one click."
                        />
                        <Feature
                            icon="🗂️"
                            title="Organise Your Collection"
                            description="Filter by author, genre, or title. Sort by rating, date added, or reading status. Your library, your rules."
                        />
                        <Feature
                            icon="📊"
                            title="Reading Statistics"
                            description="See how many books you've finished, what you're reading now, and your average rating — at a glance on your profile."
                        />
                        <Feature
                            icon="❤️"
                            title="Favourites & Wishlist"
                            description="Mark books as favourites and keep track of what you haven't started yet. Never lose a recommendation again."
                        />
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="max-w-2xl mx-auto px-6 py-16">
                <h2 className="font-serif text-3xl font-bold text-warm-800 text-center mb-3">
                    Get started in minutes
                </h2>
                <p className="text-warm-400 text-center mb-12 text-sm">Three simple steps to your perfect library.</p>
                <div className="space-y-8">
                    <Step
                        number="1"
                        title="Create your free account"
                        description="Sign up with your name and email. No credit card, no subscription — completely free."
                    />
                    <Step
                        number="2"
                        title="Search and add books"
                        description="Use the Google Books search to find any title. Add it to your personal library instantly with cover art and metadata."
                    />
                    <Step
                        number="3"
                        title="Track, rate, and enjoy"
                        description="Update your progress as you read, leave notes on memorable passages, and rate each book when you're done."
                    />
                </div>
            </section>

            {/* Final CTA */}
            <section className="bg-warm-800 py-16 px-6 text-center">
                <span className="text-5xl">📚</span>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-warm-100 mt-6 mb-4">
                    Ready to start your reading journey?
                </h2>
                <p className="text-warm-300 mb-8 text-sm max-w-md mx-auto">
                    Join readers who track their books with My Books Library.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                        to={ROUTES.REGISTER}
                        className="bg-warm-100 hover:bg-white text-warm-800 font-semibold px-8 py-4 rounded-xl text-lg transition-colors shadow-md"
                    >
                        Create Free Account
                    </Link>
                    <Link
                        to={ROUTES.LOGIN}
                        className="border border-warm-500 hover:border-warm-300 text-warm-300 hover:text-warm-100 font-semibold px-8 py-4 rounded-xl text-lg transition-colors"
                    >
                        Sign In
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-parchment border-t border-warm-200 px-6 py-6 text-center">
                <p className="text-warm-400 text-xs">
                    © {new Date().getFullYear()} My Books Library — Built with ❤️ for readers
                </p>
            </footer>
        </div>
    );
};

export default Landing;
